import axios from 'axios';
import { downloadReport, getDataFromServer } from '../../util';
import { convertFilterRangeParamsForBackend } from '../../../util/timezone';

export class TmCommonModules {
  constructor(baseURL, httpHeaders, preventionBaseUrl) {
    this.baseURL = baseURL;
    this.httpHeaders = httpHeaders;
    this.preventionBaseUrl = preventionBaseUrl;
    this.cancelTokenSource = null;
  }

  cancelRequest = (message = 'Request canceled by user') => {
    if (this.cancelTokenSource) {
      this.cancelTokenSource.cancel(message);
    }
  };

  createCancelToken = () => {
    this.cancelRequest('New request initiated');
    this.cancelTokenSource = axios.CancelToken.source();
    return this.cancelTokenSource.token;
  };

  // FIN-10470: callers build from/to in display/browser space (browser-local day
  // picks, or timeAdjustment-adjusted alert dates). Convert them to backend
  // (stored-instant) space here — exactly once per flow — so the txn list and
  // download windows match the displayed day. No-op without a timeAdjustment.
  fetchTMCommonTransactionHistoryAPI = async (payload) => {
    const { customerId, isSmartAssessment, ...params } = payload;
    const cancelToken = this.createCancelToken();
    const url = `${this.baseURL}/customers/${customerId}/transactionHistory`;

    return await getDataFromServer(
      url,
      this.httpHeaders,
      convertFilterRangeParamsForBackend(params),
      cancelToken
    );
  };

  downloadTMCommonTransactionHistoryAPI = async (payload) => {
    const { customerId, customerIds, isSmartAssessment, downloadAll, ...rawParams } = payload;
    // FIN-10470: align the date window with the displayed day (see note above).
    const params = convertFilterRangeParamsForBackend(rawParams);
    let url = '';

    if (params.caseId && !customerId) {
      // Case-level download: caseId is always present, customerIds may be empty
      // string if alerts haven't loaded yet — BE validates and returns 400 if missing
      url = `${this.baseURL}/customers/caseTransactionHistory`;
      return await getDataFromServer(url, this.httpHeaders, {
        ...params,
        ...(customerIds && { customerIds })
      });
    } else {
      url = `${this.baseURL}/customers/${customerId}/transactionHistory`;
    }

    if (downloadAll) {
      return await getDataFromServer(url, this.httpHeaders, {
        ...params,
        customerId
      });
    }

    return await downloadReport(url, this.httpHeaders, {
      ...params
    });
  };

  fetchTMCommonTransactionTriggeredTypologiesAPI = async (payload) => {
    const { alertId, ...params } = payload;
    const url = `${this.baseURL}/alerts/${alertId}/triggeredTypologies`;
    return await getDataFromServer(url, this.httpHeaders, params);
  };

  fetchTMCommonScoreExplainablity = async (payload) => {
    const { alertId, ...params } = payload;
    const url = `${this.baseURL}/alerts/${alertId}/modelScoreExplainability`;
    return await getDataFromServer(url, this.httpHeaders, params);
  };

  fetchPreventionCommonScoreExplainablity = async (payload) => {
    const { alertId, ...params } = payload;
    const url = `${this.preventionBaseUrl}/alerts/${alertId}/scoreExplanation`;
    return await getDataFromServer(url, this.httpHeaders, params);
  };

  fetchTMCommonPeerAndProfileGraph = async (payload) => {
    const { alertId, ...params } = payload;
    const url = `${this.baseURL}/alerts/${alertId}/riskIndicatorGraph`;
    return await getDataFromServer(url, this.httpHeaders, params);
  };

  fetchTypologyList = async (payload) => {
    const { alertId, ...params } = payload;
    const url = `${this.baseURL}/alerts/${alertId}/triggeredTypologies`;
    return await getDataFromServer(url, this.httpHeaders, params);
  };

  fetchVariantsListForAlertId = async (payload) => {
    const { alertId, ...params } = payload;
    const url = `${this.baseURL}/alerts/${alertId}/triggeredRules`;
    return await getDataFromServer(url, this.httpHeaders, params);
  };

  fetchRiskIndicatorList = async (payload) => {
    const { alertId, typologyId, modelId, ...params } = payload;
    const url = `${this.baseURL}/alerts/${alertId}/riskIndicators?typologyId=${typologyId}&modelId=${modelId}`;
    return await getDataFromServer(url, this.httpHeaders, params);
  };

  fetchTMCommonCustomerLoanDetails = async (payload) => {
    const { customerId, ...params } = payload;
    const url = `${this.baseURL}/customers/${customerId}/loans`;
    return await getDataFromServer(url, this.httpHeaders, params);
  };

  fetchTransactionTypesFilterAPI = async (payload) => {
    const { partyKey, from, to } = payload;
    const url = `${this.baseURL}/customers/transactions/filters/transaction-types`;
    return await getDataFromServer(url, this.httpHeaders, {
      partyKey,
      from,
      to
    });
  };
}
